import { Flex, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import {
  EDGE_COLUMN_PX,
  tableCellStylesThin,
  tableHeaderStylesThin,
} from "@/components/FinancialShared/tableStyles";
import { IHistoricoNotificacaoCcbItem } from "@/models/consignado/proposal-billings.model";
import NotificacaoIconCell from "./NotificacaoIconCell";
import { labelLinhasEvento, normalizarTipo, resolveStepState } from "./notificacaoEvento.utils";
import { renderMesCompetencia } from "./renderMesCompetencia";

type Props = {
  // Lista plana da rota GET /proposta/cobrancas/:ccb/historico-notificacoes
  // (TAR-050). Vem da mais recente pra mais antiga; aqui se reagrupa.
  notificacoes: IHistoricoNotificacaoCcbItem[];
  onReenviar: (item: IHistoricoNotificacaoCcbItem) => Promise<unknown>;
};

const HeaderDuasLinhas = ({ linha1, linha2 }: { linha1: string; linha2: string }) => (
  <>
    {linha1}
    {linha2 && (
      <>
        <br />
        {linha2}
      </>
    )}
  </>
);

const CONTRATO = "__contrato__";

type Linha = {
  chave: string;
  // null = notificações do contrato inteiro (idRef = id da proposta).
  dataCompetencia: string | null;
  itens: IHistoricoNotificacaoCcbItem[]; // da mais antiga pra mais recente
};

const tempo = (item: IHistoricoNotificacaoCcbItem) =>
  item.data_hora ? new Date(item.data_hora).getTime() : 0;

// Uma linha por competência (a primeira, "Contrato", junta os envios que não
// pertencem a competência nenhuma); os tipos de notificação viram colunas — só
// os que aparecem em alguma linha — e cada célula mostra o envio mais recente
// daquele tipo; os anteriores (reenvio, nova tentativa após falha) entram no
// popover como registro, por isso a contagem de disparos pode passar dos ícones.
export default function HistoricoNotificacoesTable({ notificacoes, onReenviar }: Props) {
  const backgroundStyle = useConditionalBackground();

  const porChave = new Map<string, Linha>();
  notificacoes.forEach((item) => {
    const chave = item.competencia?.id ?? CONTRATO;
    const linha = porChave.get(chave) ?? {
      chave,
      dataCompetencia: item.competencia?.data ?? null,
      itens: [],
    };
    linha.itens.push(item);
    porChave.set(chave, linha);
  });

  const linhas = [...porChave.values()]
    .map((linha) => ({ ...linha, itens: [...linha.itens].sort((a, b) => tempo(a) - tempo(b)) }))
    .sort((a, b) => {
      if (a.chave === CONTRATO) return -1;
      if (b.chave === CONTRATO) return 1;
      return new Date(a.dataCompetencia!).getTime() - new Date(b.dataCompetencia!).getTime();
    });

  if (linhas.length === 0) {
    return (
      <Text fontFamily="Lexend Deca" fontSize="sm" color="gray.500">
        Nenhuma notificação registrada para esta CCB.
      </Text>
    );
  }

  // Ordem das colunas = ordem cronológica média em que cada tipo dispara
  // dentro de uma linha.
  const somaPosicaoPorTipo = new Map<string, { soma: number; ocorrencias: number }>();
  linhas.forEach((linha) => {
    const vistos = new Set<string>();
    linha.itens.forEach((item, posicao) => {
      const tipo = normalizarTipo(item.tipo);
      if (vistos.has(tipo)) return;
      vistos.add(tipo);
      const atual = somaPosicaoPorTipo.get(tipo) ?? { soma: 0, ocorrencias: 0 };
      atual.soma += posicao;
      atual.ocorrencias += 1;
      somaPosicaoPorTipo.set(tipo, atual);
    });
  });

  const tiposPresentes = [...somaPosicaoPorTipo.entries()]
    .sort(([, a], [, b]) => a.soma / a.ocorrencias - b.soma / b.ocorrencias)
    .map(([tipo]) => tipo);

  return (
    <TableContainer borderRadius="lg" overflowX="auto">
      <Table variant="simple">
        <Thead {...backgroundStyle}>
          <Tr>
            <Th {...tableHeaderStylesThin} px={EDGE_COLUMN_PX} w="120px">COMPETÊNCIA</Th>
            {tiposPresentes.map((tipo) => {
              const [linha1, linha2] = labelLinhasEvento(tipo);
              return (
                <Th key={tipo} {...tableHeaderStylesThin} w="100px" whiteSpace="normal" textAlign="center">
                  <HeaderDuasLinhas linha1={linha1} linha2={linha2} />
                </Th>
              );
            })}
          </Tr>
        </Thead>
        <Tbody>
          {linhas.map((linha) => {
            const ultimo = linha.itens[linha.itens.length - 1];
            return (
              <Tr key={linha.chave} _odd={{ bg: "blackAlpha.50" }}>
                <Td {...tableCellStylesThin} px={EDGE_COLUMN_PX} textAlign="left">
                  <Text fontFamily="Lexend Deca" fontSize="14px" fontWeight="normal">
                    {linha.dataCompetencia ? renderMesCompetencia(linha.dataCompetencia) : "Contrato"}
                  </Text>
                  <Text fontFamily="Lexend Deca" fontSize="xs" color="gray.500">
                    {linha.itens.length} disparo{linha.itens.length !== 1 ? "s" : ""}
                  </Text>
                </Td>
                {tiposPresentes.map((tipo) => {
                  const daColuna = linha.itens.filter((item) => normalizarTipo(item.tipo) === tipo);
                  const maisRecente = daColuna[daColuna.length - 1];
                  return (
                    <Td key={tipo} {...tableCellStylesThin} textAlign="center">
                      {maisRecente ? (
                        <Flex justify="center">
                          <NotificacaoIconCell
                            item={maisRecente}
                            anteriores={daColuna.slice(0, -1)}
                            state={resolveStepState(maisRecente, maisRecente === ultimo)}
                            onReenviar={onReenviar}
                          />
                        </Flex>
                      ) : (
                        <Text color="gray.300">—</Text>
                      )}
                    </Td>
                  );
                })}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
}
